import { argon2id } from '@noble/hashes/argon2.js';
import { utf8 } from './bytes';
import { KEY_BYTES } from './crypto';

/**
 * The other guard: something known, for machines with no platform authenticator
 * to hold something hardware-bound.
 *
 * It exists so that "remembered" never means "openable by whoever holds the
 * disk". A browser without a passkey store would otherwise fall back to a blob
 * its own profile can open, which is the one outcome worth refusing — so this is
 * the alternative rather than a nicety.
 *
 * Weaker than the enclave in the way passphrases always are: it can be guessed
 * offline by someone holding the stored blob AND the share. Argon2id is what
 * makes that expensive rather than free, and the parameters ride the record so
 * raising them later does not strand what is already sealed.
 */

/** OWASP's baseline for interactive use, and about 0.6s of pure JS here. Enough
 *  that a cold load does not feel like a wait, and enough that guessing is a
 *  real cost per attempt. */
export const ARGON2_PARAMS = { t: 2, m: 19_456, p: 1 } as const;

export interface PassphraseParams {
  t: number;
  m: number;
  p: number;
}

/** Long enough that Argon2id is doing work on top of something, not everything.
 *  Not a policy about character classes — those push people to reuse. */
export const MIN_PASSPHRASE_CHARS = 10;

export function newPassphraseSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(16));
}

/**
 * The 32 bytes a phrase contributes to the wrap key. Same phrase, same salt and
 * same parameters is the only way back to them — a raised parameter set is a
 * different key, which is why the record carries the ones it was sealed with.
 */
export function derivePassphraseSecret(
  phrase: string,
  salt: Uint8Array,
  params: PassphraseParams = ARGON2_PARAMS,
): Uint8Array {
  return argon2id(utf8(phrase.normalize('NFKC')), salt, { ...params, dkLen: KEY_BYTES });
}
